import * as React from "react";
import orderBy from "lodash.orderby";
import moment from "moment";
import {
  DataGridHeader,
  DataGridRow,
  DataGridCell,
  Main,
  DisplaySmall,
  ModalContent,
} from "@conserveit/blocks";
import { Direction } from "utils/columns";
import { ModalSubtitle } from "./components/ModalSubtitle";
import SortArrow from "../../components/SortArrow";
import { ModalSettings } from "../../components/Reports/Workflow/components/WorkflowDatagrid";
import Loading from "../../components/Loading";
import { useFetchActions } from "./useFetchActions";
import {
  StyledModal,
  ModalTitle,
  StyledDataGrid,
  StyleDataGridHeading,
  GoToEquip,
  LoadingWrapper,
} from "./ActionListModal.styles";
interface Props {
  actionIds?: string[];
  isOpen: boolean;
  setModalVisibility: React.Dispatch<React.SetStateAction<ModalSettings>>;
}
interface Sort {
  sortKey: keyof Insite.API.Action;
  direction: Direction;
}

export const ActionListModal: React.FC<Props> = ({
  actionIds,
  isOpen,
  setModalVisibility,
}) => {
  const { loading, actions } = useFetchActions(actionIds);

  const [sort, setSort] = React.useState<Sort>({
    sortKey: "updated",
    direction: "desc",
  });

  const handleSortBy = React.useCallback(
    (key: Sort["sortKey"]) => {
      const direction = sort.direction === "asc" ? "desc" : "asc";
      setSort({
        sortKey: key,
        direction,
      });
    },
    [sort.sortKey, sort.direction]
  );

  return (
    <StyledModal
      isOpen={isOpen}
      onRequestClose={() =>
        setModalVisibility({
          isOpen: false,
          actionIds: [],
        })
      }
      title={
        <ModalTitle>
          <DisplaySmall>Closed Actions</DisplaySmall>
          {actions[0] && <ModalSubtitle action={actions[0]} />}
        </ModalTitle>
      }
    >
      <ModalContent>
        {loading && (
          <LoadingWrapper>
            <Loading />
          </LoadingWrapper>
        )}
        {actions.length > 0 && (
          <StyledDataGrid cellProps={{ type: "dense", density: "compact" }}>
            <DataGridHeader>
              <StyleDataGridHeading
                as="button"
                onClick={() => handleSortBy("key")}
                cursor="pointer"
              >
                <Main>ID</Main>
                {sort.sortKey === "key" && (
                  <SortArrow direction={sort.direction} />
                )}
              </StyleDataGridHeading>

              <StyleDataGridHeading
                as="button"
                onClick={() => handleSortBy("summary")}
                cursor="pointer"
              >
                <Main>Summary</Main>
                {sort.sortKey === "summary" && (
                  <SortArrow direction={sort.direction} />
                )}
              </StyleDataGridHeading>

              <StyleDataGridHeading
                as="button"
                onClick={() => handleSortBy("updated")}
                cursor="pointer"
              >
                <Main>Updated</Main>
                {sort.sortKey === "updated" && (
                  <SortArrow direction={sort.direction} />
                )}
              </StyleDataGridHeading>
              <StyleDataGridHeading
                as="button"
                onClick={() => handleSortBy("created")}
                cursor="pointer"
              >
                <Main>Age</Main>
                {sort.sortKey === "created" && (
                  <SortArrow direction={sort.direction} />
                )}
              </StyleDataGridHeading>
            </DataGridHeader>
            {orderBy(actions, (action) => action[sort.sortKey], [
              sort.direction,
            ]).map(({ key, summary, updated, created }) => (
              <GoToEquip
                key={key}
                to={`/actions/${key}/`}
                title="View Action"
                target="_blank"
              >
                <DataGridRow>
                  <DataGridCell>{key}</DataGridCell>
                  <DataGridCell>
                    <Main>{summary}</Main>
                  </DataGridCell>
                  <DataGridCell>
                    {moment(updated).local().fromNow()} ago
                  </DataGridCell>
                  <DataGridCell>
                    {moment(created).local().fromNow()} old
                  </DataGridCell>
                </DataGridRow>
              </GoToEquip>
            ))}
          </StyledDataGrid>
        )}
      </ModalContent>
    </StyledModal>
  );
};
